import { deriveReelSections } from './deriveReelSections';

import type { Reel } from './reelTypes';
import type { Timeline } from '@src/editor/models/Timeline';
import type { ClipMetadata } from '@src/types.ts/story';

type SubClip = NonNullable<ClipMetadata['subClips']>[number];

/**
 * PLAT-11052 — rebuilds `clipJson.subClips` from the timeline, in timeline
 * order (DE13).
 *
 * A reel has two persistence paths: `videoSource`, which is the authoritative
 * clip, and `subClips`, which the lightweight-expansion path re-crops from.
 * Leaving `subClips` as the producer first wrote it means a reel that has been
 * reordered disagrees with itself about its own order — it looks right until
 * it is expanded, and then it plays in the original order.
 *
 * Derived from Section spans rather than from elements (DS2): after word
 * edits one Section can span several elements, and `subClips` describes
 * ranges. Each entry carries the Section's **media** range, which is what the
 * expansion path crops from — not its position on the reel timeline.
 */
export function deriveSubClipsFromTimeline(
  timeline: Timeline,
  storedSubClips?: SubClip[],
  storedNames?: Reel['sections'],
): SubClip[] {
  const sections = deriveReelSections(timeline);
  const stored = matchStoredSubClips(sections, storedSubClips);

  return sections.map((section, index) => {
    const subClip: SubClip = {
      startTime: section.mediaStartTime,
      duration: section.duration,
    };

    // `subClips` carries one label; a passage crossing a speaker change is
    // attributed to whoever opens it, matching how the producer wrote it.
    const [openingSpeaker] = section.speakerLabels;
    if (openingSpeaker) {
      subClip.speakerLabel = openingSpeaker;
    }

    const match = stored[index];
    if (match?.origin?.length) subClip.origin = match.origin;

    // DE8's seed, kept current: `title` is what a Section is called when this
    // reel is combined into another one, so it mirrors the name the row shows.
    // Once the reel owns names it is the only authority — a name a creator
    // cleared has no entry, and falling back to the entry's own title there
    // would put the cleared name back on the next combine. The producer's seed
    // is read only for a reel that has never named anything.
    const title = storedNames
      ? storedNames[section.id]?.name.trim()
      : match?.title?.trim();
    if (title) subClip.title = title;

    return subClip;
  });
}

/** Media times agree to the millisecond; summed element spans drift below that. */
const sameTime = (a: number, b: number) => Math.abs(a - b) < 5e-4;

/**
 * PLAT-11067 — pairs each Section with the entry stored for it, returning one
 * per Section in order.
 *
 * The timeline knows a Section's media range but nothing a range cannot say —
 * which clip contributed it, what it was called — so both have to come from the
 * stored `subClips`. Two passes, because neither key alone is enough:
 *
 * 1. **Same media range.** A Section that was only reordered still has exactly
 *    the range it was saved with, so these pair off unambiguously — including
 *    when the reel holds the same passage twice and each copy has its own
 *    history.
 * 2. **Same media start, any length.** A Section whose words were edited has a
 *    different duration than the one stored. Matching on range alone would drop
 *    its history on the first word deleted, which is precisely when a creator
 *    most wants to know where a passage came from.
 *
 * Each stored entry is claimed once. Anything still unmatched after both passes
 * has moved too far to identify (its opening was trimmed), and is left
 * unmatched rather than given someone else's history or someone else's name.
 */
function matchStoredSubClips(
  sections: { mediaStartTime: number; duration: number }[],
  storedSubClips?: SubClip[],
): (SubClip | undefined)[] {
  const matched: (SubClip | undefined)[] = sections.map(() => undefined);
  // Only entries carrying something a range cannot say are candidates; the
  // rest have nothing to hand over.
  const unclaimed = (storedSubClips ?? []).map(sub =>
    sub.origin?.length || sub.title?.trim() ? sub : null,
  );
  if (!unclaimed.some(Boolean)) return matched;

  const claim = (
    sectionIndex: number,
    predicate: (sub: SubClip) => boolean,
  ): void => {
    const found = unclaimed.findIndex(sub => sub !== null && predicate(sub));
    const claimed = found === -1 ? null : unclaimed[found];
    if (!claimed) return;
    matched[sectionIndex] = claimed;
    unclaimed[found] = null;
  };

  sections.forEach((section, index) =>
    claim(
      index,
      sub =>
        sameTime(sub.startTime, section.mediaStartTime) &&
        sameTime(sub.duration, section.duration),
    ),
  );

  sections.forEach((section, index) => {
    if (matched[index]) return;
    claim(index, sub => sameTime(sub.startTime, section.mediaStartTime));
  });

  return matched;
}
